import React from "react";
import * as R from "ramda";
import styles from "./styles.scss";
import { connect } from "react-redux";
import { FontIcon, Div, ClockIcon } from "components/Base";
import CancelIcon from "@material-ui/icons/Cancel";
import ErrorIcon from "@material-ui/icons/Error";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import { noop, stripHtml } from "utils/General";
import { actions as TasksFlyOverActions } from "Portal/NewPortal/Sections/Home/Cards/Tasks/FlyOver/model";
import StatusCircle from "ui-kit/StatusCircle";
import { STATUS_CIRCLE_MAPPING } from "../constants";
import { useMobile } from "Portal/NewPortal/Shared/UseMobile";
import { Divider } from "EventLight/Common/FieldLayout";
import CSSModules from "react-css-modules";
import { TASK_TYPES } from "Portal/NewPortal/Sections/Home/Cards/Tasks/FlyOver/constants";
import { actions } from "Portal/NewPortal/Sections/Home/Cards/Tasks/model";
import Tooltip from "ui-kit/Tooltip";

export const truncateDescription = (str, n, useWordBoundary) => {
  if (str.length <= n) {
    return str;
  }
  const subString = str.slice(0, n - 1); // the original check
  return (
    (useWordBoundary
      ? subString.slice(0, subString.lastIndexOf(" "))
      : subString) + "..."
  );
};

export const StatusComponent = ({
  status,
  className = null,
  open_label = null,
  fontSize = 30
}) => {
  const wrappedStatus =
    status === "incomplete" && !open_label ? "denied" : status;

  return R.propOr(
    <CheckCircleIcon
      className={className}
      style={{ color: "#51CF66", fontSize }}
    />,
    wrappedStatus,
    {
      complete: <CheckCircleIcon style={{ color: "#51CF66", fontSize }} />,
      approved: <CheckCircleIcon style={{ color: "#51CF66", fontSize }} />,
      not_submitted: <ErrorIcon style={{ color: "#FF922B", fontSize }} />,
      incomplete: <ErrorIcon style={{ color: "#FF922B", fontSize }} />,
      denied: <CancelIcon style={{ color: "#FF6B6B", fontSize }} />,
      pending: <ClockIcon style={{ color: "#FF922B", fontSize }} />
    }
  );
};

const taskCompletion = status =>
  R.propOr("Complete", status, {
    not_submitted: "Incomplete",
    incomplete: "Incomplete",
    complete: "Completed",
    approved: "Completed",
    denied: "Denied",
    pending: "Pending"
  });

const DueDateTooltip = ({ isMobile, due_label }) => {
  return isMobile ? (
    <div className={styles.dueDate}>
      <FontIcon
        color="#666666"
        fs={2}
        from="material-icons-outlined"
        tooltip={due_label}
      >
        schedule
      </FontIcon>
      <div>{due_label}</div>
    </div>
  ) : (
    <div className={styles.dueDate}>
      <div>{due_label}</div>
      <FontIcon
        color="#666666"
        fs={2}
        from="material-icons-outlined"
        tooltip={due_label}
      >
        schedule
      </FontIcon>
    </div>
  );
};

const decorate = R.compose(
  connect(null, {
    openTaskFlyOver: TasksFlyOverActions.openTaskFlyOver,
    followupForm: actions.followupForm
  }),
  CSSModules(styles)
);

const Task = ({
  data = {},
  openTaskFlyOver = noop,
  followupForm = noop,
  sessionId,
  viaSessionSidebar
}) => {
  const isMobile = useMobile();

  const {
    name,
    description,
    status,
    due_label,
    is_required,
    is_past_due,
    updated_by,
    updated_at,
    type,
    open_label,
    last_updated_label,
    last_updated_date
  } = data || {};

  const isComplete = [
    "complete",
    "approved",
    type === TASK_TYPES.DOCUMENT_REQUEST ? "pending" : null
  ]
    .filter(v => v)
    .includes(status);

  const statusCircle =
    STATUS_CIRCLE_MAPPING[status] ||
    (is_past_due ? STATUS_CIRCLE_MAPPING["not-submitted"] : null);

  const StatusComp = (
    <div className={styles.button}>
      <StatusComponent status={status} open_label={open_label} fontSize={16} />
      <div className={styles.buttonText}>
        {taskCompletion(status, open_label)}
      </div>
    </div>
  );

  const showLastUpdatedLabel =
    sessionId && isComplete && last_updated_label ? true : false;
  const lastUpdatedLabel = last_updated_label
    ? `Last updated by ${last_updated_label} on ${last_updated_date}`
    : null;

  return (
    <div
      className={[styles.container, "accentable-hover"].join(" ")}
      onClick={() => {
        if (type === TASK_TYPES.FOLLOWUP_FORM) {
          followupForm({ data, taskSessionId: sessionId });
        } else {
          openTaskFlyOver({ taskData: data, taskSessionId: sessionId });
        }
      }}
    >
      <div className={styles.header}>
        <StatusCircle
          status={R.prop("status", statusCircle)}
          fontSize={isMobile ? 20 : 30}
        />
        <div className={styles.textContainer}>
          <div className={styles.title}>
            <span>{name}</span>
            {is_required && <span className={styles.required}> *</span>}
          </div>
          {description && description.length ? (
            <div className={styles.description}>
              {truncateDescription(stripHtml(description), 50, true)}
            </div>
          ) : null}
          {isMobile && due_label ? (
            <DueDateTooltip
              isMobile={isMobile}
              due_label={due_label}
              full_date={due_label}
            />
          ) : null}
          {isMobile && sessionId && last_updated_label ? (
            <div className={styles.updatedInfoMobile}>
              <FontIcon
                color="#666666"
                fs={2}
                from="material-icons-outlined"
                mr={1}
              >
                edit
              </FontIcon>
              <span>
                Updated by {last_updated_label} on {last_updated_date}
              </span>
            </div>
          ) : null}
        </div>
      </div>
      {isMobile &&
        (isComplete ||
          (sessionId && isComplete && updated_by && updated_at) ||
          (!isMobile && due_label)) && <Divider my={0} />}

      {/**  */}
      <div className={styles.footer}>
        {status ? StatusComp : null}

        {!isMobile ? (
          <Div
            display={
              viaSessionSidebar
                ? "column.flex-end.flex-end"
                : "row.flex-start.center"
            }
          >
            {showLastUpdatedLabel ? (
              <Tooltip tooltip={lastUpdatedLabel}>
                <Div
                  display="column.flex-end.center"
                  className={styles.updatedInfoContainer}
                >
                  <div className={styles.updatedInfo}>
                    {!viaSessionSidebar ? (
                      <FontIcon
                        color="#666666"
                        fs={2}
                        from="material-icons-outlined"
                        mr={1}
                      >
                        edit
                      </FontIcon>
                    ) : null}
                    <span>Updated by {last_updated_label}</span>
                    {viaSessionSidebar ? (
                      <FontIcon
                        color="#666666"
                        fs={2}
                        from="material-icons-outlined"
                        ml={1}
                      >
                        edit
                      </FontIcon>
                    ) : null}
                  </div>
                </Div>
              </Tooltip>
            ) : null}
            {showLastUpdatedLabel && due_label ? (
              viaSessionSidebar ? (
                <div></div>
              ) : (
                <>&nbsp;&middot;&nbsp;</>
              )
            ) : null}
            {due_label ? <DueDateTooltip due_label={due_label} /> : null}
          </Div>
        ) : null}
      </div>
    </div>
  );
};

export default decorate(Task);
